import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, Subject, catchError, forkJoin, map, of, switchMap, take } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Modal } from '@spirit-testing/component-library';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { PersonOption, PersonSelectComponent } from '../../../components/person-select/person-select.component';
import { TestStepTableComponent } from '../../test-case/test-step-table/test-step-table.component';
import { ModuleEditDrawerComponent } from '../module-edit-drawer/module-edit-drawer.component';
import { TestModuleService } from '../../../services/test-module.service';
import { ProjectMemberService } from '../../../services/project-member.service';
import { ProjectRoleService } from '../../../services/project-role.service';
import { AccountService } from '../../../account/account.service';
import { prefillOwnerWithCurrentUser } from '../../../shared/forms/owner-prefill';
import { ProjectMemberDTO } from '../../../models/project-member.model';
import { reviewerOptionsFromMembers } from '../../../shared/project-member-options';
import { AutomationStatus, TestStep } from '../../../models/test-case.model';
import {
  SaveFullTestModuleResponse,
  TEST_MODULE_STATUSES,
  TestModuleAutomationFramework,
  TestModuleDataSheetColumnMapping,
  TestModuleDetail,
  TestModuleParameter,
  TestModuleStatus,
  TestModuleUsage,
} from '../../../models/test-module.model';
import { TestModuleParametersCardComponent } from './parameters-card/test-module-parameters-card.component';
import { TestModuleAutomationCardComponent } from './module-automation-card/test-module-automation-card.component';
import { TestModuleColumnMappingsPanelComponent } from './column-mappings-panel/test-module-column-mappings-panel.component';
import { TestdatenblattEmbedDialogComponent } from '../testdatenblatt-embed-dialog/testdatenblatt-embed-dialog.component';
import { TestdatenblattService } from '../../../services/testdatenblatt.service';
import { Testdatenblatt } from '../../../models/testdatenblatt.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { TdbOverrideDelta, applyTdbOverride } from '../../../shared/tdb-override-delta';
import { TdbOverride } from '../../../shared/tcdl-ops';
import { createFormDirtyTracker } from '../../../shared/forms/dirty-tracker';
import { ModuleFormRaw, toSaveFullModuleRequest, withNameDefaultMappings } from './module-form.mappers';
import { warnBeforeUnload } from '../../../shared/forms/warn-before-unload';
import { findRouteParam } from '../../../shared/route-params';

/** Outcome of a single module↔sheet link write. {@code linked} true = added, false = removed. */
interface SheetLinkResult {
  sheetId: string;
  linked: boolean;
  ok: boolean;
}

@Component({
  selector: 'app-module-form',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatHint,
    MatInput,
    MatProgressSpinner,
    MatSelect,
    MatOption,
    MatChipGrid,
    MatChipRow,
    MatChipInput,
    MatChipRemove,
    SectionTitleComponent,
    PersonSelectComponent,
    TestStepTableComponent,
    TestModuleParametersCardComponent,
    TestModuleAutomationCardComponent,
    TestModuleColumnMappingsPanelComponent,
    TestdatenblattEmbedDialogComponent,
    ModuleEditDrawerComponent,
    Modal,
  ],
  templateUrl: './module-form.component.html',
  styleUrl: './module-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleFormComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly moduleService = inject(TestModuleService);
  private readonly testdatenblattService = inject(TestdatenblattService);
  private readonly projectMemberService = inject(ProjectMemberService);
  private readonly projectRoleService = inject(ProjectRoleService);
  private readonly accountService = inject(AccountService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translateService = inject(TranslateService);

  readonly isReadOnly = this.projectRoleService.isProjectReader;

  readonly projectId: string;
  moduleId: string | null;
  isEdit: boolean;

  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly saving = signal(false);
  readonly saveError = signal(false);

  /** Lokaler Draft-Zustand der Schritte (NEX-601 Buffered-Save). */
  readonly draftSteps = signal<TestStep[]>([]);
  /** Erst rendern, wenn die Ausgangs-Schritte im Draft-State stehen — sonst hydriert der Table mit []. */
  readonly stepsReady = signal(false);

  /** Öffnungs-Trigger für den Modul-Edit-Drawer; UUID des eingebundenen Sub-Moduls oder null. */
  readonly editingModuleId = signal<string | null>(null);
  /**
   * NEX-1052: Step-ID des aufrufenden MODULE_REF-Schritts, aus dem der Drawer
   * geoeffnet wurde. Der Drawer bindet dort das TDB-Override und blendet dann
   * den Inline-Editor pro verbundenem Datenblatt ein.
   */
  readonly editingContextStepId = signal<string | null>(null);
  readonly editingContextStep = computed<TestStep | null>(() => {
    const stepId = this.editingContextStepId();
    if (!stepId) return null;
    return this.draftSteps().find((s) => s.id === stepId) ?? null;
  });
  readonly editingContextOverride = computed<TdbOverride | null>(() => {
    const stepId = this.editingContextStepId();
    if (!stepId) return null;
    return this.stepTable()?.overrideDraftFor(stepId) ?? null;
  });
  /** NEX-1052 Follow-up: alle Testdatenblaetter des Projekts fuer den Drawer. */
  readonly availableSheets = signal<readonly Testdatenblatt[]>([]);
  private readonly stepTable = viewChild<TestStepTableComponent>('stepTable');

  /** Deklarierte Schnittstelle (NEX-704), Teil des Modul-Draft-Zustands. */
  readonly draftParameters = signal<TestModuleParameter[]>([]);
  readonly draftReturns = signal<TestModuleParameter[]>([]);
  /** Card meldet ungültige/duplizierte Namen — blockiert Save bis behoben. */
  readonly parametersInvalid = signal(false);

  readonly draftColumnMappings = signal<TestModuleDataSheetColumnMapping[]>([]);

  /** Verbundene Testdatenblätter (Draft-only, NEX-524). */
  readonly linkedSheets = signal<Testdatenblatt[]>([]);
  private linkedSheetsLoaded = true;
  readonly showTdbDialog = signal(false);
  readonly tdbExcludeIds = computed(() => this.linkedSheets().map((s) => s.id));
  /** Beim Laden verbundene Datenblatt-IDs — Basis für das Save-Diff. */
  private initialLinkedIds = new Set<string>();

  readonly users = signal<ProjectMemberDTO[]>([]);
  readonly personOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.users()));
  /**
   * NEX-824: cached current-user id, resolved once regardless of edit/create —
   * the component is reused across route param changes (route reuse, NEX-602),
   * so `applyAuthorDefaultIfCreating` re-checks `isEdit` at call time rather than
   * refetching the profile on every navigation into create mode.
   */
  private readonly currentUserId = signal<string | null>(null);

  readonly statusOptions = TEST_MODULE_STATUSES;
  readonly fieldLengths = FIELD_LENGTHS;
  readonly moduleKey = signal('');
  readonly automationKey = signal<string | null>(null);

  readonly usageTestCases = signal<TestModuleUsage[]>([]);

  readonly form = this.fb.group({
    title: ['', [Validators.required, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]],
    description: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    status: this.fb.control<TestModuleStatus>('DRAFT', Validators.required),
    authorUserId: ['', Validators.required],
    tags: this.fb.control<string[]>([]),
    automationStatus: this.fb.control<AutomationStatus>('MANUAL', Validators.required),
    automationFramework: this.fb.control<TestModuleAutomationFramework | null>(null),
  });

  readonly moduleLabel = computed(() => this.moduleKey());

  /**
   * Value-based dirty tracking (NEX-603): form value plus the buffered step
   * drafts (eingebundene Module sind seit NEX-644 MODULE_REF-Schritte und stecken
   * in {@code draftSteps}). Reverting a change back to the loaded state clears the
   * dirty pill and re-disables save (NEX-601). The result is mirrored onto
   * {@code form.dirty} so template, guard and save-bar keep reading the flag.
   */
  private readonly dirtyTracker = createFormDirtyTracker(this.form, {
    extra: () => ({
      steps: this.stepsSignature(this.draftSteps()),
      sheets: JSON.stringify([...this.linkedSheets().map((s) => s.id)].sort()),
      parameters: this.parametersSignature(this.draftParameters()),
      returns: this.parametersSignature(this.draftReturns()),
      columnMappings: this.columnMappingsSignature(this.draftColumnMappings()),
    }),
  });

  saveDisabled(): boolean {
    return (
      this.saving() ||
      this.form.invalid ||
      this.parametersInvalid() ||
      (this.isEdit && !this.form.dirty) ||
      this.incompleteConditionSteps().length > 0
    );
  }

  incompleteConditionSteps(): number[] {
    return this.stepTable()?.incompleteConditionStepNumbers() ?? [];
  }

  /** Sichtbarkeit der App-Modal für die Verlassen-Bestätigung (NEX-814, ersetzt den nativen Browser-Dialog). */
  readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.projectId = this.requireParam('projectId');
    this.moduleId = this.findParam('moduleId');
    this.isEdit = this.moduleId !== null;

    effect(() => {
      if (this.isReadOnly()) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    });
  }

  ngOnInit(): void {
    this.loadUsers();
    this.loadCurrentUserId();
    this.loadAvailableSheets();
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.syncDirtyFromContent());

    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => this.initForModule(params.get('moduleId')));
  }

  /**
   * NEX-1052 Follow-up: laedt die projektweiten Testdatenblaetter fuer Drawer und
   * Step-Table. Aenderungen aus dem Drawer kommen nicht ueber diesen Weg zurueck,
   * sondern als `sheetsChanged`-Snapshot direkt in `availableSheets` (kein
   * HTTP-Round-Trip). Ein Fehler wird still geschluckt — ohne Blaetter fehlen
   * lediglich die Chips, das Formular bleibt bedienbar.
   */
  loadAvailableSheets(): void {
    this.testdatenblattService
      .list(this.projectId)
      .pipe(
        catchError(() => of([] as Testdatenblatt[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sheets) => this.availableSheets.set(sheets));
  }

  private initForModule(moduleId: string | null): void {
    this.moduleId = moduleId;
    this.isEdit = moduleId !== null;
    this.draftSteps.set([]);
    this.draftParameters.set([]);
    this.draftReturns.set([]);
    this.draftColumnMappings.set([]);
    this.parametersInvalid.set(false);
    this.linkedSheets.set([]);
    this.linkedSheetsLoaded = moduleId === null;
    this.initialLinkedIds = new Set();
    this.stepsReady.set(false);
    this.automationKey.set(null);

    if (moduleId) {
      this.loadModule(moduleId);
      return;
    }
    this.form.reset({
      title: '',
      description: '',
      status: 'DRAFT',
      authorUserId: '',
      tags: [],
      automationStatus: 'MANUAL',
      automationFramework: null,
    });
    this.stepsReady.set(true);
    this.applyAuthorDefaultIfCreating();
    this.snapshotInitialState();
  }

  // ── Grunddaten / Sidebar ──────────────────────────────────────────────
  addTag(event: MatChipInputEvent): void {
    const value = (event.value ?? '').trim();
    const tagsControl = this.form.controls.tags;
    if (value && !tagsControl.value.includes(value)) {
      tagsControl.setValue([...tagsControl.value, value]);
      tagsControl.markAsDirty();
    }
    event.chipInput.clear();
  }

  removeTag(tag: string): void {
    const tagsControl = this.form.controls.tags;
    tagsControl.setValue(tagsControl.value.filter((t) => t !== tag));
    tagsControl.markAsDirty();
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.saving() || this.incompleteConditionSteps().length > 0) {
      return;
    }
    this.saving.set(true);
    this.saveError.set(false);

    const raw = this.form.getRawValue() as ModuleFormRaw;
    const request = toSaveFullModuleRequest(
      raw,
      this.draftSteps(),
      this.draftParameters(),
      this.draftReturns(),
      withNameDefaultMappings(
        this.draftColumnMappings(),
        this.linkedSheets(),
        this.draftParameters().map((p) => p.name),
        this.linkedSheetsLoaded,
      ),
    );

    const request$ =
      this.isEdit && this.moduleId
        ? this.moduleService.saveFull(this.projectId, this.moduleId, request)
        : this.moduleService.createFull(this.projectId, request);

    request$
      .pipe(
        switchMap((response) =>
          this.persistSheetLinks(response.module.id).pipe(map((linkResults) => ({ response, linkResults }))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ response, linkResults }) => {
          this.applyClientIdMapping(response);
          this.saving.set(false);

          // Baseline reflects what actually persisted, so a retry re-attempts
          // only the failed links and unlink detection stays correct (NEX-524).
          const persisted = new Set(this.initialLinkedIds);
          for (const r of linkResults) {
            if (!r.ok) continue;
            if (r.linked) persisted.add(r.sheetId);
            else persisted.delete(r.sheetId);
          }
          this.initialLinkedIds = persisted;

          this.moduleKey.set(response.module.key);
          this.automationKey.set(response.module.automationKey ?? null);
          if (linkResults.some((r) => !r.ok)) {
            // Module saved, but at least one sheet link failed. The module now
            // exists — switch to edit mode and keep the user here to retry.
            this.moduleId = response.module.id;
            this.isEdit = true;
            this.saveError.set(true);
            return;
          }

          this.form.markAsPristine();
          if (this.isEdit) {
            this.snapshotInitialState();
            return;
          }
          this.router.navigate(['/projects', this.projectId, 'test-modules']);
        },
        error: () => {
          this.saving.set(false);
          this.saveError.set(true);
        },
      });
  }

  onStepsChanged(steps?: TestStep[]): void {
    if (steps !== undefined) {
      this.draftSteps.set(steps);
    }
    this.syncDirtyFromContent();
  }

  onParametersChanged(parameters: TestModuleParameter[]): void {
    this.draftParameters.set(parameters);
    this.syncDirtyFromContent();
  }

  onReturnsChanged(returns: TestModuleParameter[]): void {
    this.draftReturns.set(returns);
    this.syncDirtyFromContent();
  }

  onParametersInvalid(invalid: boolean): void {
    this.parametersInvalid.set(invalid);
  }

  onColumnMappingsChanged(mappings: TestModuleDataSheetColumnMapping[]): void {
    this.draftColumnMappings.set(mappings);
    this.syncDirtyFromContent();
  }

  inputParametersForMapping(): TestModuleParameter[] {
    return this.draftParameters();
  }

  onSheetMappingsChanged(sheetId: string, next: TestModuleDataSheetColumnMapping[]): void {
    const others = this.draftColumnMappings().filter((m) => m.testdatenblattId !== sheetId);
    const own = next.filter((m) => m.testdatenblattId === sheetId);
    this.onColumnMappingsChanged([...others, ...own]);
  }

  onCancel(): void {
    this.navigateToList();
  }

  hasUnsavedChanges(): boolean {
    return this.form.dirty && !this.saving();
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  // ── Eingebundenes Modul öffnen (NEX-644) ──────────────────────────────
  /**
   * Sprung aus einer MODULE_REF-Zeile zum eingebundenen Modul. Da Ziel- und
   * Quell-Route identisch sind (nur Param-Wechsel), feuert der CanDeactivate-Guard
   * nicht — die Dirty-Bestätigung muss hier selbst erfolgen (NEX-602).
   */
  onOpenModule(moduleId: string): void {
    if (!this.form.dirty) {
      this.navigateToModule(moduleId);
      return;
    }
    this.confirmDeactivate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.navigateToModule(moduleId);
        }
      });
  }

  private navigateToModule(moduleId: string): void {
    this.router.navigate(['/projects', this.projectId, 'test-modules', moduleId, 'edit']);
  }

  /**
   * Bearbeiten eines eingebundenen Sub-Moduls im Right-Drawer, ohne den aktuellen
   * Modul-Editor zu verlassen. Die MODULE_REF-Zeilen spiegeln die Änderung nach dem
   * Save live über den gemeinsamen TestModuleStore.
   */
  onEditModule(event: { moduleId: string; stepId: string | null }): void {
    // Kontext-Step ZUERST setzen, damit das computed `editingContextStep` bereits
    // aktualisiert ist, wenn der Drawer-Effect wegen moduleId-Wechsel feuert.
    this.editingContextStepId.set(event.stepId);
    this.editingModuleId.set(event.moduleId);
  }

  onModuleDrawerClosed(): void {
    this.editingModuleId.set(null);
    this.editingContextStepId.set(null);
  }

  /**
   * NEX-1052 Follow-up: Der Drawer emittiert das Delta, wir patchen den
   * aufrufenden MODULE_REF-Schritt im Draft-Zustand + im Step-Table.
   *
   * <p>Das Override lebt bewusst im Draft und persistiert erst beim regulaeren
   * Save des Outer-Formulars — der `stepsSignature`-Fix macht den Speichern-
   * Knopf dafuer klickbar. Ein automatischer Save beim Drawer-`saved`-Event
   * wurde bewusst nicht verdrahtet (NEX-1051-Review: `finishSaveSuccess`
   * navigiert bedingungslos zur Liste, im Anlege-Modus faellt der `dirty`-Guard
   * weg — beide Effekte kaepern den Nutzer aus dem Editor).
   */
  onTdbOverrideApplied(delta: TdbOverrideDelta): void {
    this.draftSteps.set(applyTdbOverride(this.draftSteps(), delta));
    const { stepId, ...patch } = delta;
    this.stepTable()?.patchStepOverride(stepId, patch);
    this.syncDirtyFromContent();
  }

  private applyClientIdMapping(response: SaveFullTestModuleResponse): void {
    // Parameter sind rein gesteuert: die vom Server zurückgegebenen Zeilen (echte
    // UUIDs, kein clientId) ersetzen den Draft, damit ein Folge-Save ohne Reload nicht
    // erneut als Neuanlage läuft und in den UNIQUE(name)-Guard rennt.
    this.draftParameters.set(response.inputs ?? []);
    this.draftReturns.set(response.outputs ?? []);
    this.draftColumnMappings.set(response.columnMappings ?? []);

    const mapping = response.clientIdMapping;
    if (!mapping || Object.keys(mapping).length === 0) {
      return;
    }
    this.draftSteps.update((list) => list.map((s) => (mapping[s.id] ? { ...s, id: mapping[s.id] } : s)));
  }

  // ── Laden ─────────────────────────────────────────────────────────────
  private loadUsers(): void {
    this.projectMemberService
      .getMembers(this.projectId)
      .pipe(
        catchError(() => {
          this.snackBar.open(this.translateService.instant('modules.drawer.usersLoadError'), undefined, {
            duration: 4000,
          });
          return of([] as ProjectMemberDTO[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users) => {
        this.users.set(users);
        this.applyAuthorDefaultIfCreating();
      });
  }

  private loadCurrentUserId(): void {
    this.accountService
      .getProfile()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((profile) => {
        this.currentUserId.set(profile?.id ?? null);
        this.applyAuthorDefaultIfCreating();
      });
  }

  /**
   * NEX-824: prefills the author with the current user when creating a new
   * module. Called from three points (users loaded, profile loaded, create
   * route entered) since any of them may resolve last — the underlying helper
   * is idempotent (no-op once the field is touched or already filled).
   */
  private applyAuthorDefaultIfCreating(): void {
    if (this.isEdit) return;
    const userId = this.currentUserId();
    if (!userId) return;
    prefillOwnerWithCurrentUser(this.form.controls.authorUserId, userId, this.personOptions());
    this.snapshotInitialState();
  }

  private loadModule(id: string): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.moduleService
      .getById(this.projectId, id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (module) => {
          this.applyModule(module);
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private applyModule(module: TestModuleDetail): void {
    this.moduleKey.set(module.key);
    this.automationKey.set(module.automationKey ?? null);
    this.form.patchValue({
      title: module.name,
      description: module.description ?? '',
      status: module.status,
      authorUserId: module.authorUserId ?? '',
      tags: module.tags ?? [],
      automationStatus: module.automationStatus ?? 'MANUAL',
      automationFramework: module.automationFramework ?? null,
    });
    this.draftSteps.set(module.steps ?? []);
    this.draftParameters.set(module.inputs ?? []);
    this.draftReturns.set(module.outputs ?? []);
    this.draftColumnMappings.set(module.columnMappings ?? []);
    this.parametersInvalid.set(false);
    this.stepsReady.set(true);
    this.form.markAsPristine();
    this.snapshotInitialState();
    this.loadLinkedSheets(module.id);
    this.loadUsage(module.id);
  }

  private loadUsage(moduleId: string): void {
    this.moduleService
      .listUsage(this.projectId, moduleId)
      .pipe(
        catchError(() => of([] as TestModuleUsage[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((usage) => this.usageTestCases.set(usage));
  }

  onJumpToUsageTestCase(testCaseId: string): void {
    this.router.navigate(['/projects', this.projectId, 'test-cases', testCaseId, 'edit']);
  }

  // ── Verbundene Testdatenblätter (Draft-only, NEX-524) ─────────────────
  private loadLinkedSheets(moduleId: string): void {
    this.testdatenblattService
      .list(this.projectId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sheets) => {
        this.linkedSheetsLoaded = sheets !== null;
        const linked = (sheets ?? []).filter((s) => s.testModuleIds.includes(moduleId));
        this.linkedSheets.set(linked);
        this.initialLinkedIds = new Set(linked.map((s) => s.id));
        this.snapshotInitialState();
      });
  }

  onTdbApplied(sheets: Testdatenblatt[]): void {
    if (sheets.length === 0) return;
    const existing = new Set(this.linkedSheets().map((s) => s.id));
    const additions = sheets.filter((s) => !existing.has(s.id));
    if (additions.length === 0) return;
    this.linkedSheets.set([...this.linkedSheets(), ...additions]);
    this.syncDirtyFromContent();
  }

  /**
   * NEX-1050-Review: Sekundär-Aktion im Verbinden-Dialog — Navigation
   * zum Anlegen-Formular. Dialog schliesst erst NACH bestätigter Navigation.
   * Der Route-Guard {@code canDeactivateModuleForm} kann bei dirty form ablehnen;
   * würden wir vorab schliessen, verlöre der Nutzer beim „Abbrechen" im Verwerfen-
   * Dialog seine Auswahl (und die Meldung „Erfolgreich angelegt" würde bei
   * Nicht-Navigation trotzdem suggeriert).
   */
  /**
   * NEX-1050: „Neues Datenblatt anlegen" verlaesst den Modul-Editor. Der
   * CanDeactivate-Guard kann das ablehnen (dirty form) — dann muss der Dialog
   * zurueckkommen, samt Suchtext und Haekchen.
   *
   * <p>Waehrend der Navigation blenden wir ihn aus: Die Verwerfen-Rueckfrage des
   * Guards ist selbst ein {@code lib-modal} mit demselben z-index. Beide stuenden
   * nebeneinander sichtbar, und ein Klick auf „Bleiben" gibt den Body-Scroll wieder
   * frei, obwohl der TDB-Dialog noch offen ist. Der Zustand geht dabei nicht
   * verloren — der Dialog leert Suche und Auswahl nur in seinem eigenen
   * {@code close()}, das ausschliesslich aus Uebernehmen/Abbrechen laeuft.
   */
  onCreateNewSheet(): void {
    this.showTdbDialog.set(false);
    void this.router
      .navigate(['/projects', this.projectId, 'testdatenblaetter', 'neu'])
      .then((ok) => {
        if (!ok) {
          this.showTdbDialog.set(true);
        }
      })
      // Resolver oder Guard koennen werfen; ohne catch bliebe eine unbehandelte
      // Rejection und der Nutzer saesse vor einem verschwundenen Dialog.
      .catch(() => this.showTdbDialog.set(true));
  }

  onRemoveSheet(sheet: Testdatenblatt): void {
    this.linkedSheets.set(this.linkedSheets().filter((s) => s.id !== sheet.id));
    this.draftColumnMappings.set(this.draftColumnMappings().filter((m) => m.testdatenblattId !== sheet.id));
    this.syncDirtyFromContent();
  }

  /**
   * Schreibt nur die Differenz zum zuletzt gespeicherten Stand, je Blatt über den
   * Verknüpfungs-Endpunkt dieses einen Moduls. Blatt-Inhalt und Verknüpfungen
   * anderer Module bleiben so unberührt.
   */
  private persistSheetLinks(moduleId: string): Observable<SheetLinkResult[]> {
    const currentIds = new Set(this.linkedSheets().map((s) => s.id));
    const ops: Observable<SheetLinkResult>[] = [];

    for (const id of currentIds) {
      if (!this.initialLinkedIds.has(id)) {
        ops.push(this.writeSheetLink(id, moduleId, true));
      }
    }
    for (const id of this.initialLinkedIds) {
      if (!currentIds.has(id)) {
        ops.push(this.writeSheetLink(id, moduleId, false));
      }
    }

    return ops.length === 0 ? of([]) : forkJoin(ops);
  }

  /** Wirft nie: ein Fehlschlag kommt als {@code ok: false}, damit die übrigen Blätter trotzdem gespeichert werden. */
  private writeSheetLink(sheetId: string, moduleId: string, linked: boolean): Observable<SheetLinkResult> {
    const write$ = linked
      ? this.testdatenblattService.linkModule(this.projectId, sheetId, moduleId)
      : this.testdatenblattService.unlinkModule(this.projectId, sheetId, moduleId);
    return write$.pipe(
      map(() => ({ sheetId, linked, ok: true })),
      catchError(() => of({ sheetId, linked, ok: false })),
    );
  }

  private snapshotInitialState(): void {
    this.dirtyTracker.reset();
    this.form.markAsPristine();
  }

  private syncDirtyFromContent(): void {
    if (this.dirtyTracker.isDirtyNow()) {
      this.form.markAsDirty();
    } else {
      this.form.markAsPristine();
    }
  }

  private stepsSignature(steps: TestStep[]): string {
    return JSON.stringify(
      steps.map((s) => ({
        id: s.id,
        // NEX-644: kind + calledModuleId erfassen, damit hinzugefügte/entfernte
        // MODULE_REF-Zeilen (eingebundene Module) das Dirty-Flag auslösen.
        kind: s.kind,
        calledModuleId: s.calledModuleId ?? null,
        title: s.title ?? null,
        action: s.action ?? null,
        expectedResult: s.expectedResult ?? null,
        notes: s.notes ?? null,
        tcdl: s.tcdl ?? null,
        status: s.status,
        libraryStepId: s.libraryStepId ?? null,
        // NEX-1051: TDB-Override am Modulaufruf gehoert zum Dirty-Vergleich, sonst
        // bleibt Speichern disabled, wenn nur Zeilenauswahl/Bedingung geaendert wird.
        dataSheetId: s.dataSheetId ?? null,
        dataSheetFilters: s.dataSheetFilters ?? null,
        stepCondition: s.stepCondition ?? null,
      })),
    );
  }

  // Nur die persistenz-relevanten Felder (inkl. Reihenfolge) fließen ins Dirty-Signal;
  // clientId/id bleiben außen vor, damit Add→Save→Re-Seed nicht fälschlich dirty bleibt.
  private parametersSignature(list: TestModuleParameter[]): string {
    return JSON.stringify(
      list.map((p) => ({
        name: p.name,
        type: p.type,
        required: p.required,
        defaultValue: p.defaultValue ?? null,
      })),
    );
  }

  private columnMappingsSignature(list: TestModuleDataSheetColumnMapping[]): string {
    return JSON.stringify(
      [...list]
        .sort((a, b) => {
          const c = a.testdatenblattId.localeCompare(b.testdatenblattId);
          return c !== 0 ? c : a.sheetColumn.localeCompare(b.sheetColumn);
        })
        .map((m) => ({
          testdatenblattId: m.testdatenblattId,
          sheetColumn: m.sheetColumn,
          parameterName: m.parameterName,
        })),
    );
  }

  private navigateToList(): void {
    this.router.navigate(['/projects', this.projectId, 'test-modules']);
  }

  private requireParam(name: string): string {
    const value = this.findParam(name);
    if (!value) throw new Error(`Pflicht-Route-Parameter fehlt: ${name}`);
    return value;
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}
